import { Injectable } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  IHttpConnectionOptions,
  ILogger,
  LogLevel as SignalRLogLevel,
} from '@microsoft/signalr';
import { BehaviorSubject, combineLatest, concat, EMPTY, firstValueFrom, from, Observable, of, Subject, timer } from 'rxjs';
import {
  catchError,
  concatAll,
  concatMap,
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  first,
  map,
  switchMap,
  takeUntil,
  tap,
  timeout,
} from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IClientSideLogger, LogLevel } from '@nimbus-library/features/logger/services/client-logger/i-client-side-logger';
import { ForkRequestsService } from '../shared/fork-requests.service';

interface ISignalRConnectionData {
  url: string;
  idToken: string;
  refreshTick?: number;
}

class SignalRLogger implements ILogger {
  public constructor(private readonly logger?: IClientSideLogger) {}
  public log(logLevel: SignalRLogLevel, message: string): void {
    let nimbusLogLevel = LogLevel.Info;

    switch (logLevel) {
      case SignalRLogLevel.Critical:
      case SignalRLogLevel.Error:
        nimbusLogLevel = LogLevel.Error;
        break;
      case SignalRLogLevel.Warning:
        nimbusLogLevel = LogLevel.Warn;
        break;
      case SignalRLogLevel.Information:
        nimbusLogLevel = LogLevel.Info;
        break;
      case SignalRLogLevel.Debug:
      case SignalRLogLevel.Trace:
        nimbusLogLevel = LogLevel.Debug;
        break;
    }

    this.logger?.writeMessage(nimbusLogLevel, 'SignalR', message);
  }
}

@UntilDestroy()
@Injectable()
export abstract class BaseSignalRHub {
  private readonly stopTimeoutMs: number = 2000;
  private readonly tokenKeepAliveIntervalMs: number = 15_000;
  private readonly tokenlessReconnectIntervalMs: number = 10 * 60_000;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private signalRRetryIntervals: number[] = [0, 5000, 10000, 30000, 60000, 300000];
  private connection?: HubConnection;
  private negotiatedIdToken?: string;
  private connectionDataIdToken?: string;
  public isConnected$ = new BehaviorSubject<boolean>(undefined);
  public onConnected$ = new Subject<boolean>();
  public onLoggedOut$ = new Subject<void>();

  protected abstract signalRUrl: string;

  public constructor(
    protected readonly forkRequestService: ForkRequestsService,
    protected readonly signalRDiagnosticClient: boolean = false,
    protected readonly clientSideLogger?: IClientSideLogger,
  ) {}

  protected abstract onConnected(): void;
  protected abstract onConnectedFailed(): void;
  protected abstract subscribeEvents(): void;
  protected abstract unsubscribeEvents(): void;

  protected abstract acquireIdToken(): Observable<string>; // getting access token in async way it fires only once

  protected invoke<TResult>(methodName: string): Observable<TResult> {
    return this.invokeConnectionMethod(methodName);
  }

  protected invokeWithArguments<TResult>(methodName: string, ...arg: any[]): Observable<TResult> {
    return this.invokeConnectionMethod(methodName, [], ...arg);
  }

  protected invokeBatchWithArguments<TResult>(methodName: string, batchArgs: any[], ...args: any[]): Observable<TResult> {
    return this.invokeConnectionMethod(methodName, batchArgs, ...args);
  }

  protected initialize(isReadyToConnect$: Observable<boolean>): void {
    this.isConnected$
      .pipe(
        filter((x) => x),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.onConnected();
      });

    const signalRConnectionData$ = this.getSignalRConnectionData$(isReadyToConnect$);
    // Connection logic
    // 1. isReadyToConnect$ - when user is logged in and has SignalR feature turned on
    // 2. isConnected$ - when connection is established
    // 3. token refresh - when token is refreshed we need to reconnect to make sure always the latest token is used
    combineLatest([isReadyToConnect$, this.isConnected$, signalRConnectionData$])
      .pipe(
        distinctUntilChanged(isEqual),
        switchMap(([isReadyToConnect, isConnected, signalRConnectionData]: [boolean, boolean, ISignalRConnectionData]) => {
          if (!isReadyToConnect && isConnected) {
            // user logged out or SignalR feature turned off
            this.logInfo('Stop connection on is not ready to connect');
            this.onLoggedOut$.next();
            return this.stopConnection();
          } else if (isReadyToConnect) {
            // user logged in and SignalR feature turned on but connection is not established
            if (
              signalRConnectionData &&
              signalRConnectionData.url &&
              (!isConnected || this.isTokenRefreshedSinceConnect(signalRConnectionData.idToken))
            ) {
              this.logInfo('Stop and start connection');
              return concat(this.stopConnection(), this.startConnection(signalRConnectionData));
            }
          }

          return of(null);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  protected requiresIdToken(): boolean {
    return true;
  }

  protected getConnectionOptions(accessTokenFactory: () => Promise<string>): IHttpConnectionOptions {
    return { accessTokenFactory };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected subscribeEvent(methodName: string, newMethod: (...args: any[]) => any): void {
    this.connection.on(methodName, newMethod);
  }

  protected unsubscribeEvent(methodName: string): void {
    this.connection.off(methodName);
  }

  private startConnection(signalRConnectionData: ISignalRConnectionData): Observable<boolean> {
    const retryIntervals = [0, ...this.signalRRetryIntervals];
    let retryAttempt = 0;
    return from(retryIntervals).pipe(
      concatMap((x) =>
        of(x).pipe(
          delay(x),
          concatMap(async () => {
            try {
              retryAttempt++;
              this.logInfo(`Start SignalR connection attempt ${retryAttempt} of ${retryIntervals.length}.`);
              await this.startSignalRConnection(signalRConnectionData);
              const isConnected = this.connection?.state === HubConnectionState.Connected;
              this.isConnected$.next(isConnected);
              if (isConnected || retryIntervals.length === retryAttempt) {
                this.onConnected$.next(isConnected);
                if (!isConnected) {
                  this.onConnectedFailed();
                }
              }
              return of(isConnected);
            } catch (err) {
              this.logError(`Start SignalR connection error: ${err.message}`);
              await firstValueFrom(this.stopConnection().pipe(first()));
              return of(false);
            }
          }),
        ),
      ),
      concatAll(),
      takeUntil(this.onConnected$), // once it's connected we close this stream
      takeUntil(this.onLoggedOut$), // logged out close the stream
    );
  }

  private async startSignalRConnection(signalRConnectionData: ISignalRConnectionData): Promise<void> {
    let negotiatedIdTokenPromise: Promise<string>;
    this.connection = new HubConnectionBuilder()
      .withUrl(
        signalRConnectionData.url,
        this.getConnectionOptions(() => (negotiatedIdTokenPromise ??= this.acquireNegotiatedIdToken())),
      )
      .configureLogging(new SignalRLogger(this.clientSideLogger))
      .build();
    this.connectionDataIdToken = signalRConnectionData.idToken;
    this.subscribeEventsInternal();

    await this.connection.start(); // connected with the token, retry turned off
  }

  // The token captured in signalRConnectionData can be hours old after a sleep or long idle; negotiate with a freshly acquired one.
  private async acquireNegotiatedIdToken(): Promise<string> {
    this.negotiatedIdToken = await firstValueFrom(this.acquireIdToken());
    return this.negotiatedIdToken;
  }

  private isTokenRefreshedSinceConnect(idToken: string): boolean {
    return idToken !== this.negotiatedIdToken && idToken !== this.connectionDataIdToken;
  }

  private stopConnection(): Observable<void> {
    if (!this.connection) {
      return of(null);
    }
    this.unsubscribeEventsInternal();
    return from(this.connection.stop()).pipe(
      timeout(this.stopTimeoutMs),
      catchError(() => of(null)),
      finalize(() => {
        this.connection = undefined;
        this.negotiatedIdToken = undefined;
        this.connectionDataIdToken = undefined;
      }),
    );
  }

  private subscribeEventsInternal(): void {
    if (!this.connection) {
      return;
    }
    this.unsubscribeEventsInternal();
    this.connection.onreconnecting(this.onReconnecting.bind(this));
    this.connection.onreconnected(this.onReconnected.bind(this));
    this.connection.onclose(this.onClose.bind(this));
    this.subscribeEvents();
  }

  private unsubscribeEventsInternal(): void {
    if (!this.connection) {
      return;
    }
    this.connection.onreconnecting(() => {});
    this.connection.onreconnected(() => {});
    this.connection.onclose(() => {});
    this.unsubscribeEvents();
  }

  private onReconnecting(): void {
    this.isConnected$.next(false);
    this.logInfo('Event - onReconnecting.');
  }

  private onReconnected(): void {
    this.isConnected$.next(true);
    this.logInfo('Event - onReconnected.');
  }

  private onClose(): void {
    this.isConnected$.next(false);
    this.logInfo('Event - onClose.');
  }

  private getSignalRConnectionData$(isReadyToConnect$: Observable<boolean>): Observable<ISignalRConnectionData> {
    if (!this.requiresIdToken()) {
      return isReadyToConnect$.pipe(
        switchMap((isReadyToConnect) => (isReadyToConnect ? timer(0, this.tokenlessReconnectIntervalMs) : EMPTY)),
        map((refreshTick): ISignalRConnectionData => ({ url: this.getSignalRUrl(), idToken: undefined, refreshTick })),
      );
    }

    return isReadyToConnect$.pipe(
      switchMap((isReadyToConnect) => (isReadyToConnect ? timer(0, this.tokenKeepAliveIntervalMs) : EMPTY)),
      switchMap(() => this.acquireIdToken()),
      distinctUntilChanged(),
      tap(() => this.logInfo('Connection data has been recreated because the token had been changed.')),
      map((idToken: string): ISignalRConnectionData => ({ url: this.getSignalRUrl(), idToken })),
      catchError(() => of(null)),
    );
  }

  private getSignalRUrl(): string {
    const url = new URL(`${window.location.origin}/${this.signalRUrl}`);
    if (this.signalRDiagnosticClient) {
      url.searchParams.append('diagnosticClient', this.signalRDiagnosticClient.toString());
    }
    return url.toString();
  }

  private logInfo(message: string): void {
    this.clientSideLogger?.writeMessage(LogLevel.Info, 'SignalR', message);
  }

  private logError(message: string): void {
    this.clientSideLogger?.writeMessage(LogLevel.Error, 'SignalR', message);
  }

  private invokeConnectionMethod<T>(methodName: string, batchArgs: any[] = [], ...args: any[]): Observable<T> {
    if (!this.connection) {
      return;
    }
    if (!batchArgs?.length) {
      return from(this.connection.invoke(methodName, ...args));
    }
    return this.forkRequestService.forkAndConcatRequest(batchArgs, (x) => from(this.connection.invoke(methodName, x, ...args)), 500);
  }
}
